import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getProjectBySku } from '../redux/slice/project.slice';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Thumbs, EffectCreative, EffectFade, Mousewheel, EffectCards, EffectCoverflow, EffectCube, EffectFlip, } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-creative';
import 'swiper/css/effect-fade';
import {
    MapPin, Calendar, Play, Download, Phone, Send, ArrowLeft, FileText, LayoutGrid,
    Building2, Ruler, Utensils, Bath, Layers, ExternalLink, ChevronLeft, ChevronRight
} from 'lucide-react';
import Container from '../components/common/Container';
import { RevealText } from '../components/common/RevealText';
import { AMENITIES_LIST, getAmenityIcon } from '../constants/amenities';

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%', minHeight: '320px' };

const VIEWPORT = { once: true, amount: 0.2 };
const EASE = [0.22, 0.61, 0.36, 1];

// Content stagger animation for elements inside sections
const CONTENT_VARIANTS = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.07, delayChildren: 0.12 },
    },
};
const CONTENT_ITEM = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0 },
};
const CONTENT_VIEWPORT = { once: true, amount: 0.5 };

// Different animation per section
const SECTION_ANIMATIONS = {
    overview: {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: VIEWPORT,
        transition: { duration: 0.55, ease: EASE },
    },
    amenities: {
        initial: { opacity: 0, scale: 0.96 },
        whileInView: { opacity: 1, scale: 1 },
        viewport: VIEWPORT,
        transition: { duration: 0.6, ease: EASE },
    },
    specifications: {
        initial: { opacity: 0, x: -32 },
        whileInView: { opacity: 1, x: 0 },
        viewport: VIEWPORT,
        transition: { duration: 0.5, ease: EASE },
    },
    blueprints: {
        initial: { opacity: 0, x: -32 },
        whileInView: { opacity: 1, x: 0 },
        viewport: VIEWPORT,
        transition: { duration: 0.5, ease: EASE },
    },
    gallery: {
        initial: { opacity: 0, y: 40 },
        whileInView: { opacity: 1, y: 0 },
        viewport: VIEWPORT,
        transition: { duration: 0.7, ease: EASE },
    },
    location: {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: VIEWPORT,
        transition: { duration: 0.55, ease: EASE },
    },
};

// Luxury Cinematic Hero Slider
const ProjectHeroSlider = ({ images, title }) => {
    return (
        <header className="relative h-[85vh] md:h-[95vh] overflow-hidden">
            <Swiper
                modules={[Autoplay, EffectFade, Pagination]}
                effect="fade"
                speed={2000}
                loop={images.length > 1}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                pagination={{
                    clickable: true,
                    renderBullet: (index, className) => {
                        return `<span class="${className} !w-12 !h-[2px] !rounded-none !bg-white/30 transition-all duration-500 overflow-hidden relative">
                            <i class="absolute inset-0 bg-secondary origin-left scale-x-0 transition-transform duration-[5s] linear active-bullet-progress"></i>
                        </span>`;
                    },
                }}
                className="w-full h-full hero-swiper"
            >
                {images.slice(0, 5).map((img, index) => (
                    <SwiperSlide key={index}>
                        <div className="relative w-full h-full">
                            {/* Zooming Background Image */}
                            <motion.img
                                initial={{ scale: 1.1 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 6, ease: "linear" }}
                                src={img.url}
                                alt={`${title} - Hero ${index + 1}`}
                                className="w-full h-full object-cover"
                            />

                            {/* Multi-layered Overlays */}
                            <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-transparent to-primary/80" />
                            <div className="absolute inset-0 bg-black/30" />

                            {/* Animated Content */}
                            <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4">
                                <div className="max-w-5xl">
                                    <motion.div
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 1, delay: 0.5 }}
                                    >
                                        <span className="inline-block px-4 py-1.5 mb-6 text-[10px] md:text-xs font-bold uppercase tracking-[0.5em] text-secondary border border-secondary/30 rounded-full backdrop-blur-sm bg-black/20">
                                            The Art of Fine Living
                                        </span>
                                        <h1 className="text-5xl md:text-7xl lg:text-9xl font-serif font-bold text-white mb-6 tracking-tight">
                                            <RevealText text={title} className="justify-center" />
                                        </h1>
                                        <div className="flex items-center justify-center gap-6">
                                            <div className="h-[1px] w-12 md:w-20 bg-secondary/50" />
                                            <p className="text-white/80 text-sm md:text-xl uppercase tracking-[0.3em] font-light">
                                                Redefining Luxury
                                            </p>
                                            <div className="h-[1px] w-12 md:w-20 bg-secondary/50" />
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>

            {/* Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3"
            >
                <span className="text-[10px] text-white/40 uppercase tracking-[0.3em] font-bold">Discover More</span>
                <div className="w-[1px] h-12 bg-gradient-to-b from-secondary to-transparent" />
            </motion.div>

            {/* Custom CSS for Progress Bullets */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .hero-swiper .swiper-pagination-bullet-active i {
                    animation: fillProgress 5s linear forwards;
                }
                @keyframes fillProgress {
                    from { transform: scaleX(0); }
                    to { transform: scaleX(1); }
                }
                .hero-swiper .swiper-pagination {
                    bottom: 40px !important;
                    display: flex;
                    justify-content: center;
                    gap: 12px;
                }
            `}} />
        </header>
    );
};

// Luxury Cinematic Gallery Slider v4 - "The Panoramic Narrative"
const ProjectGallerySlider = ({ images, title }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const swiperRef = useRef(null);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [isVertical, setIsVertical] = useState(window.innerWidth >= 768);
    const isProgrammaticScroll = useRef(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsVertical(window.innerWidth >= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sync Thumbs with Main Slider (e.g. for Autoplay or Navigation Click)
    useEffect(() => {
        if (thumbsSwiper && !thumbsSwiper.destroyed && activeIndex !== thumbsSwiper.realIndex) {
            isProgrammaticScroll.current = true;
            thumbsSwiper.slideToLoop(activeIndex);

            // Clear previous timeout if exists
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            // Reset flag after transition finishes
            timeoutRef.current = setTimeout(() => {
                isProgrammaticScroll.current = false;
            }, 1050); // duration + buffer
        }
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [activeIndex, thumbsSwiper]);

    return (
        <div className="relative w-full  bg-background overflow-hidden">
            {/* Massive Background Stroke Text for Depth */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden h-full">
                <AnimatePresence mode="wait">
                    <motion.h2
                        key={activeIndex}
                        initial={{ opacity: 0, scale: 0.8, x: 100 }}
                        animate={{ opacity: 0.1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 1.2, x: -100 }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        className="text-[20vw] font-serif font-black uppercase text-transparent stroke-primary/90 stroke-1 whitespace-nowrap"
                        style={{ WebkitTextStroke: '1px rgba(62,54,46,0.1)' }}
                    >
                        {String(activeIndex + 1).padStart(2, '0')} Perspective
                    </motion.h2>
                </AnimatePresence>
            </div>

            {/* Subtle Grain Overlay for Cinematic Feel */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-20" style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/p6.png')" }} />

            <Container className="relative z-10">
                <div className="flex flex-col gap-8 md:gap-12">

                    {/* Header Section: Interactive Title & Counter */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                        <div className="md:col-span-9 space-y-6">
                            {/* <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                className="inline-flex items-center gap-3 px-3 py-1 bg-secondary/10 border border-secondary/20 rounded-sm"
                            >
                                <div className="w-1 h-1 rounded-full bg-secondary" />
                                <span className="text-secondary text-[10px] uppercase font-bold tracking-[0.3em]">Cinematic Feed</span>
                            </motion.div> */}

                            <h3 className="text-3xl font-serif text-primary font-bold tracking-tight leading-tight">
                                Visual  <span>Signature</span>
                            </h3>

                            <p className="text-secondary text-base font-sans leading-relaxed max-w-2xl">
                                Experience the intersection of light and matter. Every angle is a testament to architectural precision.
                            </p>
                        </div>

                        <div className="hidden lg:flex lg:col-span-3 lg:justify-end">
                            {/* Progress Ring / Counter */}
                            <div className="flex items-center gap-6">
                                <div className="relative w-12 h-12 flex items-center justify-center">
                                    <svg className="w-full h-full -rotate-90">
                                        <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1" fill="transparent" className="text-primary/10" />
                                        <circle
                                            cx="24"
                                            cy="24"
                                            r="22"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            fill="transparent"
                                            className="text-secondary"
                                            strokeDasharray={2 * Math.PI * 22}
                                            strokeDashoffset={2 * Math.PI * 22 * (1 - (activeIndex + 1) / images.length)}
                                            style={{ transition: 'stroke-dashoffset 0.8s ease-out' }}
                                        />
                                    </svg>
                                    <span className="absolute text-[10px] font-bold text-primary">{activeIndex + 1}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-primary text-[10px] uppercase font-bold tracking-widest">Active Scene</span>
                                    <span className="text-secondary font-semibold  text-sm font-serif italic">The {title} Series</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sliders Section: Main + Thumbnails Side-by-Side */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">

                        {/* Left Column: The 3D Canvas */}
                        <div className="md:col-span-10  relative group">
                            <div className="relative aspect-[16/10] md:aspect-[21/10] rounded-xl overflow-hidden transition-shadow duration-1000">
                                <Swiper
                                    onSwiper={(swiper) => { swiperRef.current = swiper; }}
                                    onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                                    modules={[Navigation, Pagination, Autoplay, EffectCube, Thumbs]}
                                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                                    effect={'creative'}
                                    creativeEffect={{
                                        prev: {
                                            shadow: true,
                                            translate: [0, 0, -800],
                                            rotate: [0, -120, 0],
                                            opacity: 0,
                                        },
                                        next: {
                                            shadow: true,
                                            translate: ['120%', 0, 0],
                                        },
                                    }}
                                    speed={1000}
                                    loop={images.length > 1}
                                    autoplay={images.length > 1 ? { delay: 3000, disableOnInteraction: false } : false}
                                    className="w-full h-full"
                                >
                                    {images.map((img, index) => (
                                        <SwiperSlide key={img._id || index}>
                                            <div className="relative w-full h-full overflow-hidden">
                                                <motion.img
                                                    animate={{
                                                        scale: index === activeIndex ? 1.05 : 1.3,
                                                        x: index === activeIndex ? 0 : 40,
                                                        rotate: index === activeIndex ? 0 : 2
                                                    }}
                                                    transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                                                    src={img.url}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />



                                                {/* Phase Tag */}
                                                <div className="absolute bottom-12 left-12 overflow-hidden">
                                                    <motion.div
                                                        initial={{ y: "100%" }}
                                                        animate={{ y: index === activeIndex ? 0 : "100%" }}
                                                        transition={{ duration: 0.8, delay: 0.5 }}
                                                        className="flex items-center gap-4"
                                                    >
                                                        <span className="text-secondary font-black text-6xl opacity-20 font-serif">0{index + 1}</span>
                                                        <div className="h-px w-20 bg-secondary/50" />
                                                    </motion.div>
                                                </div>
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>

                                {/* Custom Navigation Brackets */}
                                <div className="absolute z-30 flex items-center 
                                    md:inset-y-12 md:right-12 md:flex-col md:justify-between md:py-6
                                    max-md:inset-x-4 max-md:top-1/2 max-md:-translate-y-1/2 max-md:flex-row pointer-events-none">
                                    <div className="w-px h-1/3 bg-white/10 md:block hidden" />
                                    <div className="flex gap-6 md:flex-col flex-row max-md:w-full max-md:justify-between pointer-events-auto">
                                        <button
                                            onClick={() => swiperRef.current?.slidePrev()}
                                            className="w-14 h-14 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl text-white flex items-center justify-center hover:bg-secondary hover:text-primary transition-all duration-500 shadow-2xl group/btn"
                                        >
                                            <ChevronLeft size={24} className="group-hover/btn:-translate-x-1 transition-transform" />
                                        </button>
                                        <button
                                            onClick={() => swiperRef.current?.slideNext()}
                                            className="w-14 h-14 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl text-white flex items-center justify-center hover:bg-secondary hover:text-white transition-all duration-500 shadow-2xl group/btn"
                                        >
                                            <ChevronRight size={24} className="group-hover/btn:translate-x-1 transition-transform" />
                                        </button>
                                    </div>
                                    <div className="w-px h-1/3 bg-white/10 md:block hidden" />
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Full Height Thumbnails */}
                        <div className="hidden md:block md:col-span-2 relative w-full h-auto min-h-0">
                            <div className="md:absolute md:inset-0 w-full h-full">
                                <Swiper
                                    onSwiper={setThumbsSwiper}
                                    loop={true}
                                    spaceBetween={12}
                                    slidesPerView={'auto'}
                                    centeredSlides={true}
                                    slideToClickedSlide={true}
                                    watchSlidesProgress={true}
                                    mousewheel={true}
                                    direction={isVertical ? "vertical" : "horizontal"}
                                    modules={[Navigation, Thumbs, Mousewheel]}
                                    onSlideChange={(swiper) => {
                                        if (isProgrammaticScroll.current) return;
                                        if (swiperRef.current && swiperRef.current.realIndex !== swiper.realIndex) {
                                            swiperRef.current.slideToLoop(swiper.realIndex);
                                        }
                                    }}
                                    className="h-full w-full "
                                >
                                    {images.map((img, index) => (
                                        <SwiperSlide key={img._id || index} style={{ width: 'auto', height: 'auto' }}>
                                            <button
                                                className={`relative flex-shrink-0 group overflow-hidden rounded-xl transition-all duration-500 w-full aspect-video h-auto ${index === activeIndex ? 'grayscale-0 border-2 border-secondary opacity-100 scale-100' : 'grayscale opacity-40 scale-90 border-2 border-transparent'}`}
                                            >
                                                <img src={img.url} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                                <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors" />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className={`text-[10px] font-bold text-white transition-opacity duration-500 ${index === activeIndex ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                        {String(index + 1).padStart(2, '0')}
                                                    </span>
                                                </div>
                                            </button>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

// Normalize date from API (string or { $date: string })
const formatProjectDate = (dateObj) => {
    if (!dateObj) return null;
    const str = typeof dateObj === 'string' ? dateObj : dateObj?.$date;
    if (!str) return null;
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short' });
};

// Format area (e.g. 1175150 -> "11.75 Lakh sq.ft" or similar)
const formatArea = (sqFt) => {
    if (sqFt == null || sqFt === '') return null;
    const num = Number(sqFt);
    if (isNaN(num)) return null;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} Lakh sq.ft`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)} K sq.ft`;
    return `${num} sq.ft`;
};

const ProjectDetailsPage = () => {
    const { sku } = useParams();
    const dispatch = useDispatch();
    const { project, loading, error } = useSelector((state) => state.project);
    const navigate = useNavigate();

    const overviewRef = useRef(null);
    const amenitiesRef = useRef(null);
    const specificationsRef = useRef(null);
    const blueprintsRef = useRef(null);
    const galleryRef = useRef(null);
    const locationRef = useRef(null);

    const { isLoaded: isMapLoaded, loadError: mapLoadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    });

    const scrollToSection = (ref) => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        if (sku) {
            dispatch(getProjectBySku(sku));
        }
    }, [dispatch, sku]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-primary font-serif tracking-widest uppercase text-sm">Loading Project...</p>
                </div>
            </div>
        );
    }

    if (error || !project) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-primary p-4">
                <h2 className="text-4xl font-serif mb-4">Project Not Found</h2>
                <p className="text-gray-500 mb-6">{error || "The project you requested is unavailable."}</p>
                <button
                    onClick={() => navigate('/projects')}
                    className="px-6 py-2 bg-primary text-white rounded hover:bg-secondary transition-colors"
                >
                    Back to Projects
                </button>
            </div>
        );
    }

    const amenitiesWithIcons = (project.amenities || [])
        .map((name) => {
            const icon = getAmenityIcon(name);
            return { name, icon };
        })
        .filter((a) => a.icon);

    const startDateFormatted = formatProjectDate(project.startDate);
    const areaFormatted = formatArea(project.projectArea);
    const locationAddress = project.location?.address || 'Surat, Gujarat';
    const brochureUrl = project.brochure?.url;

    const navSections = [
        { label: 'Overview', ref: overviewRef },
        { label: 'Amenities', ref: amenitiesRef },
        { label: 'Specifications', ref: specificationsRef },
        { label: 'Blueprints', ref: blueprintsRef },
        { label: 'Gallery', ref: galleryRef },
        { label: 'Location & Enquiries', ref: locationRef },
    ];

    const handleDownloadBrochure = async (e) => {
        e.preventDefault();
        if (!brochureUrl) return;

        try {
            const response = await fetch(brochureUrl);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = brochureUrl.split('/').pop() || `${project.title}-Brochure`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            window.open(brochureUrl, '_blank');
        }
    };

    return (
        <div className="bg-background min-h-screen text-primary font-sans selection:bg-secondary selection:text-white">

            {/* Hero */}
            {/* <ProjectHeroSlider images={project.images} title={project.title} /> */}
            {/* Hero */}
            <header className="relative h-[80vh] md:h-[90vh]">
                <img
                    src={project.images?.[0]?.url || "https://via.placeholder.com/1920x1080"}
                    alt={project.title}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40" />
                <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1
                            className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-4"
                        // style={{
                        //     textShadow: "0px 1px 0px #c9c9c9, 0px 2px 0px #b9b9b9, 0px 3px 0px #a9a9a9, 0px 4px 0px #999, 0px 5px 0px #888, 0px 6px 1px rgba(0,0,0,0.1), 0px 0px 5px rgba(0,0,0,0.1), 0px 1px 3px rgba(0,0,0,0.3), 0px 3px 5px rgba(0,0,0,0.2), 0px 5px 10px rgba(0,0,0,0.25), 0px 10px 10px rgba(0,0,0,0.2), 0px 20px 20px rgba(0,0,0,0.15)"
                        // }}
                        >
                            <RevealText text={project.title} />
                        </h1>
                        <p className="text-background/70 text-lg md:text-xl uppercase tracking-[0.2em] font-semibold mb-6">
                            Live A Cut Above The Rest
                        </p>
                        {/* <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-white/90 text-sm md:text-base">
                            <span className="flex items-center gap-2">
                                <MapPin size={18} className="text-secondary" /> {locationAddress}
                            </span>
                            {startDateFormatted && (
                                <>
                                    <span className="hidden md:inline">|</span>
                                    <span className="flex items-center gap-2">
                                        <Calendar size={18} className="text-secondary" /> Start: {startDateFormatted}
                                    </span>
                                </>
                            )}
                            {project.status && (
                                <>
                                    <span className="hidden md:inline">|</span>
                                    <span className="px-2 py-0.5 bg-white/20 rounded text-xs uppercase tracking-wider">
                                        {project.status}
                                    </span>
                                </>
                            )}
                            {project.bookingStatus && (
                                <span className="px-2 py-0.5 bg-secondary/80 rounded text-xs uppercase tracking-wider">
                                    Booking {project.bookingSta tus}
                                </span>
                            )}
                        </div> */}
                    </motion.div>
                </div>
            </header>

            {/* Sticky nav */}
            <div className="sticky top-[78px] z-20 bg-background border-b border-primary/10 shadow-sm py-3 hidden md:block">
                <Container>
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-primary/70 hover:text-secondary transition-colors"
                        >
                            <ArrowLeft size={16} /> Back
                        </button>
                        <div className="flex justify-center gap-4 xl:gap-6 text-xs xl:text-sm font-bold uppercase tracking-wider text-primary/70 flex-wrap">
                            {navSections.map(({ label, ref }) => (
                                <button
                                    key={label}
                                    onClick={() => scrollToSection(ref)}
                                    className="hover:text-secondary transition-colors"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="w-16" />
                    </div>
                </Container>
            </div>

            {/* Overview */}
            <motion.section
                ref={overviewRef}
                initial={SECTION_ANIMATIONS.overview.initial}
                whileInView={SECTION_ANIMATIONS.overview.whileInView}
                viewport={SECTION_ANIMATIONS.overview.viewport}
                transition={SECTION_ANIMATIONS.overview.transition}
                className="py-16 md:py-20 bg-surface"
            >
                <Container>
                    <motion.div
                        className="grid grid-cols-1 lg:grid-cols-12 gap-12"
                        variants={CONTENT_VARIANTS}
                        initial="hidden"
                        whileInView="visible"
                        viewport={CONTENT_VIEWPORT}
                    >
                        <motion.div className="lg:col-span-8" variants={CONTENT_ITEM}>
                            <h3 className="text-secondary font-sans text-sm font-bold uppercase tracking-widest mb-2">The Details</h3>
                            <h2 className="text-4xl font-serif font-bold text-primary mb-6">Project Overview</h2>
                            <div className="prose prose-lg text-secondary font-medium leading-relaxed text-justify mb-8">
                                <p>{project.description}</p>
                            </div>
                            {project.configuration && project.configuration.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    <span className="text-sm font-semibold text-primary mr-2">Configurations:</span>
                                    {project.configuration.map((cfg, i) => (
                                        <span
                                            key={i}
                                            className="px-3 py-1 bg-primary text-white text-sm font-medium rounded"
                                        >
                                            {cfg}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="mt-8 flex flex-wrap gap-6 text-sm text-tertiary">
                                {areaFormatted && (
                                    <div className="flex items-center gap-2">
                                        <Ruler size={18} className="text-secondary" />
                                        <span className="font-medium"><strong className="text-primary">Area:</strong> {areaFormatted}</span>
                                    </div>
                                )}
                                {startDateFormatted && (
                                    <div className="flex items-center gap-2">
                                        <Calendar size={18} className="text-secondary" />
                                        <span className="font-medium"><strong className="text-primary">Started:</strong> {startDateFormatted}</span>
                                    </div>
                                )}
                                {project.status && (
                                    <div className="flex items-center gap-2">
                                        <Building2 size={18} className="text-secondary" />
                                        <span className="font-medium"><strong className="text-primary">Status:</strong> {project.status}</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                        <motion.div className="lg:col-span-4" variants={CONTENT_ITEM}>
                            <div className="bg-background p-6 shadow-xl rounded-sm space-y-4 border-t-4 border-secondary sticky top-32">
                                <button
                                    onClick={() => navigate('/contact', { state: { selectedProject: project._id } })}
                                    className="w-full py-4 bg-secondary text-white font-bold uppercase tracking-widest hover:bg-primary transition-colors flex items-center justify-between px-6"
                                >
                                    <span>Enquire Now</span>
                                    <Send size={18} />
                                </button>
                                {brochureUrl ? (
                                    <a
                                        href={brochureUrl}
                                        onClick={handleDownloadBrochure}
                                        className="w-full py-4 bg-primary text-white font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors flex items-center justify-between px-6 cursor-pointer"
                                    >
                                        <span>Download Brochure</span>
                                        <Download size={18} />
                                    </a>
                                ) : (
                                    <button
                                        disabled
                                        className="w-full py-4 bg-light/30 text-light font-bold uppercase tracking-widest cursor-not-allowed flex items-center justify-between px-6"
                                    >
                                        <span>Brochure not available</span>
                                        <FileText size={18} />
                                    </button>
                                )}
                                {/* {project.sku && (
                                    <div className="p-4 bg-gray-100 text-center rounded text-sm text-gray-500">
                                        <p>Project SKU</p>
                                        <p className="font-mono text-xs mt-1">{project.sku}</p>
                                    </div>
                                )} */}
                            </div>
                        </motion.div>
                    </motion.div>
                </Container>
            </motion.section>

            {/* Video / second image */}
            {/* {(project.images?.length > 1) && (
                <section className="py-16 bg-white">
                    <Container>
                        <div className="text-center mb-10">
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-2">
                                <span className="text-secondary">Visual</span> Walkthrough
                            </h2>
                            <p className="text-gray-500 max-w-2xl mx-auto">
                                Experience the grandeur of {project.title} through our visuals.
                            </p>
                        </div>
                        <div className="relative aspect-video rounded-lg overflow-hidden group cursor-pointer shadow-2xl">
                            <img
                                src={project.images[1]?.url || project.images[0]?.url}
                                alt="Walkthrough"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
                                    <Play size={32} fill="white" />
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>
            )} */}

            {/* Amenities */}
            <motion.section
                ref={amenitiesRef}
                initial={SECTION_ANIMATIONS.amenities.initial}
                whileInView={SECTION_ANIMATIONS.amenities.whileInView}
                viewport={SECTION_ANIMATIONS.amenities.viewport}
                transition={SECTION_ANIMATIONS.amenities.transition}
                className="py-16 md:py-20 bg-background"
            >
                <Container>
                    <motion.div
                        variants={CONTENT_VARIANTS}
                        initial="hidden"
                        whileInView="visible"
                        viewport={CONTENT_VIEWPORT}
                    >
                        <motion.div className="text-center mb-12" variants={CONTENT_ITEM}>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary">Amenities & Facilities</h2>
                            <div className="w-24 h-1 bg-secondary mx-auto mt-4" />
                        </motion.div>
                        {amenitiesWithIcons.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 text-center">
                                {amenitiesWithIcons.map(({ name, icon: Icon }, i) => (
                                    <motion.div
                                        key={i}
                                        variants={CONTENT_ITEM}
                                        className="flex flex-col items-center gap-3 group hover:-translate-y-2 transition-transform duration-300"
                                    >
                                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-surface border-2 border-primary/10 flex items-center justify-center text-secondary shadow-sm group-hover:bg-secondary group-hover:text-white group-hover:border-secondary transition-all">
                                            {Icon && <Icon size={28} className="md:w-8 md:h-8" />}
                                        </div>
                                        <span className="font-medium text-tertiary text-xs md:text-sm uppercase tracking-wide leading-tight">
                                            {name}
                                        </span>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <motion.p className="text-center text-light" variants={CONTENT_ITEM}>Amenities information will be updated soon.</motion.p>
                        )}
                    </motion.div>
                </Container>
            </motion.section>

            {/* Specifications: structure, flooring, kitchen, bathroom */}
            <motion.section
                ref={specificationsRef}
                initial={SECTION_ANIMATIONS.specifications.initial}
                whileInView={SECTION_ANIMATIONS.specifications.whileInView}
                viewport={SECTION_ANIMATIONS.specifications.viewport}
                transition={SECTION_ANIMATIONS.specifications.transition}
                className="py-16 md:py-20 bg-background text-primary border-t border-primary/5"
            >
                <Container>
                    <motion.div
                        variants={CONTENT_VARIANTS}
                        initial="hidden"
                        whileInView="visible"
                        viewport={CONTENT_VIEWPORT}
                    >
                        <motion.div className="text-center mb-12" variants={CONTENT_ITEM}>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary">Specifications</h2>
                            <div className="w-24 h-1 bg-secondary mx-auto mt-4" />
                        </motion.div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8  mx-auto">
                            {project.structure && project.structure.length > 0 && (
                                <motion.div className="bg-white p-6 rounded-lg border border-primary/10 shadow-lg" variants={CONTENT_ITEM}>
                                    <div className="flex items-center gap-2 text-secondary mb-4">
                                        <Layers size={22} />
                                        <h3 className="font-serif text-xl font-bold text-primary">Structure</h3>
                                    </div>
                                    <ul className="space-y-2 text-primary/80 text-sm">
                                        {project.structure.map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-base text-secondary">
                                                <span className="text-secondary mt-1">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                            {project.flooring && project.flooring.length > 0 && (
                                <motion.div className="bg-white p-6 rounded-lg border border-primary/10 shadow-lg" variants={CONTENT_ITEM}>
                                    <div className="flex items-center gap-2 text-secondary mb-4">
                                        <LayoutGrid size={22} />
                                        <h3 className="font-serif  text-primary text-xl font-bold">Flooring</h3>
                                    </div>
                                    <ul className="space-y-2 text-primary/80 text-sm">
                                        {project.flooring.map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-base text-secondary">
                                                <span className="text-secondary mt-1">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                            {project.kitchen && project.kitchen.length > 0 && (
                                <motion.div className="bg-white p-6 rounded-lg border border-primary/10 shadow-lg" variants={CONTENT_ITEM}>
                                    <div className="flex items-center gap-2 text-secondary mb-4">
                                        <Utensils size={22} />
                                        <h3 className="font-serif   text-primary text-xl font-bold">Kitchen</h3>
                                    </div>
                                    <ul className="space-y-2 text-primary/80 text-sm">
                                        {project.kitchen.map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-base text-secondary">
                                                <span className="text-secondary mt-1">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                            {project.bathroom && project.bathroom.length > 0 && (
                                <motion.div className="bg-white p-6 rounded-lg border border-primary/10 shadow-lg" variants={CONTENT_ITEM}>
                                    <div className="flex items-center gap-2 text-secondary mb-4">
                                        <Bath size={22} />
                                        <h3 className="font-serif  text-primary text-xl font-bold">Bathroom</h3>
                                    </div>
                                    <ul className="space-y-2 text-primary/80 text-sm">
                                        {project.bathroom.map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-base text-secondary">
                                                <span className="text-secondary mt-1">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                        </div>
                        {!(project.structure?.length || project.flooring?.length || project.kitchen?.length || project.bathroom?.length) && (
                            <motion.p className="text-center text-primary/60 mt-6" variants={CONTENT_ITEM}>Specification details will be updated soon.</motion.p>
                        )}
                    </motion.div>
                </Container>
            </motion.section>

            {/* Blueprints */}
            <motion.section
                ref={blueprintsRef}
                initial={SECTION_ANIMATIONS.blueprints.initial}
                whileInView={SECTION_ANIMATIONS.blueprints.whileInView}
                viewport={SECTION_ANIMATIONS.blueprints.viewport}
                transition={SECTION_ANIMATIONS.blueprints.transition}
                className="py-16 md:py-20 bg-surface"
            >
                <Container>
                    <motion.div
                        variants={CONTENT_VARIANTS}
                        initial="hidden"
                        whileInView="visible"
                        viewport={CONTENT_VIEWPORT}
                    >
                        <motion.div className="text-center mb-12" variants={CONTENT_ITEM}>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary">Master Plan & Blueprints</h2>
                            <div className="w-24 h-1 bg-secondary mx-auto mt-4" />
                        </motion.div>
                        {project.bluePrint && project.bluePrint.length > 0 ? (
                            project.bluePrint.length === 1 ? (
                                <motion.div className="flex justify-center" variants={CONTENT_ITEM}>
                                    <div className="rounded-lg overflow-hidden border-2 border-primary/10 bg-background shadow-lg relative group w-full max-w-xl">
                                        <a
                                            href={project.bluePrint[0].url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block aspect-[4/3] overflow-hidden"
                                        >
                                            <img
                                                src={project.bluePrint[0].url}
                                                alt="Master plan / Blueprint"
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                <span className="opacity-0 group-hover:opacity-100 text-white flex items-center gap-2 font-medium uppercase tracking-wider text-sm">
                                                    <ExternalLink size={18} /> View full size
                                                </span>
                                            </div>
                                        </a>
                                    </div>
                                </motion.div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6  mx-auto">
                                    {project.bluePrint.map((bp, index) => (
                                        <motion.div key={bp._id || index} variants={CONTENT_ITEM} className="rounded-lg overflow-hidden border-2 border-primary/10 bg-background shadow-lg relative group">
                                            <a
                                                href={bp.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block aspect-[4/3] overflow-hidden"
                                            >
                                                <img
                                                    src={bp.url}
                                                    alt={`Blueprint ${index + 1}`}
                                                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                                />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                    <span className="opacity-0 group-hover:opacity-100 text-white flex items-center gap-2 font-medium uppercase tracking-wider text-sm">
                                                        <ExternalLink size={18} /> View full size
                                                    </span>
                                                </div>
                                            </a>
                                        </motion.div>
                                    ))}
                                </div>
                            )
                        ) : (
                            <motion.div className="border-2 border-primary/10 p-8 rounded-lg bg-background shadow-sm max-w-2xl mx-auto text-center" variants={CONTENT_ITEM}>
                                <LayoutGrid className="w-12 h-12 text-light mx-auto mb-4" />
                                <p className="text-light uppercase tracking-widest text-sm">Floor plans coming soon</p>
                            </motion.div>
                        )}
                    </motion.div>
                </Container>
            </motion.section>

            {/* Gallery */}
            <motion.section
                ref={galleryRef}
                initial={SECTION_ANIMATIONS.gallery.initial}
                whileInView={SECTION_ANIMATIONS.gallery.whileInView}
                viewport={SECTION_ANIMATIONS.gallery.viewport}
                transition={SECTION_ANIMATIONS.gallery.transition}
                className="py-16 md:py-20 bg-background text-primary"
            >
                <Container>
                    <motion.div
                        variants={CONTENT_VARIANTS}
                        initial="hidden"
                        whileInView="visible"
                        viewport={CONTENT_VIEWPORT}
                    >
                        <motion.div className="text-center mb-12" variants={CONTENT_ITEM}>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary">Project Gallery</h2>
                            <p className="text-secondary text-lg font-medium mt-2">A glimpse into your future home.</p>
                             <div className="w-24 h-1 bg-secondary mx-auto mt-4" />
                        </motion.div>
                        {project.images && project.images.length > 0 ? (
                            <motion.div variants={CONTENT_ITEM}>
                                <ProjectGallerySlider images={project.images} title={project.title} />
                            </motion.div>
                        ) : (
                            <motion.p className="text-center text-primary/50 italic" variants={CONTENT_ITEM}>Gallery updates incoming...</motion.p>
                        )}
                    </motion.div>
                </Container>
            </motion.section>

            {/* Location & Enquiries: left = enquiry, right = map with theme pin */}
            <motion.section
                ref={locationRef}
                initial={SECTION_ANIMATIONS.location.initial}
                whileInView={SECTION_ANIMATIONS.location.whileInView}
                viewport={SECTION_ANIMATIONS.location.viewport}
                transition={SECTION_ANIMATIONS.location.transition}
                className="py-16 md:py-20 bg-surface"
            >
                <Container>
                    <motion.div
                        variants={CONTENT_VARIANTS}
                        initial="hidden"
                        whileInView="visible"
                        viewport={CONTENT_VIEWPORT}
                    >
                        <motion.div className="text-center mb-12" variants={CONTENT_ITEM}>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary">Location & Enquiries</h2>
                            <p className="text-secondary font-sans font-medium mt-2 max-w-xl mx-auto">Visit us or get in touch for site visits and bookings.</p>
                            <div className="w-24 h-1 bg-secondary mx-auto mt-4" />
                        </motion.div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch mx-auto">
                            {/* Left: Enquiries */}
                            <motion.div className="flex flex-col order-2 lg:order-1" variants={CONTENT_ITEM}>
                                <div className="bg-background rounded-sm border-t-4 border-secondary shadow-lg p-6 md:p-8 h-full flex flex-col">
                                    <h3 className="text-xl font-serif font-bold text-primary mb-6 uppercase tracking-wide">Get in touch</h3>

                                    {project.location?.address && (
                                        <div className="flex items-start gap-4 mb-6">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-secondary">
                                                <MapPin size={20} />
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Project location</p>
                                                <p className="text-tertiary text-sm leading-relaxed">{project.location.address}</p>
                                                {project.location.lat != null && project.location.lng != null && (
                                                    <a
                                                        href={`https://www.google.com/maps?q=${project.location.lat},${project.location.lng}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1.5 mt-2 text-secondary text-sm font-medium hover:underline"
                                                    >
                                                        Open in Google Maps <ExternalLink size={14} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {project.officeAddress && (
                                        <div className="flex items-start gap-4 mb-6">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-secondary">
                                                <Building2 size={20} />
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Office address</p>
                                                <p className="text-secondary font-sans text-sm font-medium leading-relaxed">{project.officeAddress}</p>
                                            </div>
                                        </div>
                                    )}

                                    {project.officePhoneNumbers && project.officePhoneNumbers.length > 0 && (
                                        <div className="flex items-start gap-4 mb-6">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-secondary">
                                                <Phone size={20} />
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-2">Phone</p>
                                                <div className="flex flex-wrap gap-3">
                                                    {project.officePhoneNumbers.map((num, i) => (
                                                        <a
                                                            key={i}
                                                            href={`tel:${num}`}
                                                            className="text-secondary hover:text-secondary font-medium text-sm border-b border-transparent hover:border-secondary transition-colors"
                                                        >
                                                            {num}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-auto pt-6">
                                        <button
                                            onClick={() => navigate('/contact', { state: { selectedProject: project._id } })}
                                            className="w-full py-4 bg-secondary text-white font-bold uppercase tracking-widest hover:bg-primary transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Send size={18} /> Enquire now
                                        </button>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Right: Map via Google Maps NPM - no iframe panel */}
                            <motion.div className="order-1 lg:order-2 min-h-[320px] md:min-h-[400px]" variants={CONTENT_ITEM}>
                                <div className="relative h-full min-h-[320px] md:min-h-[400px] rounded-sm overflow-hidden border-2 border-primary/10 shadow-xl bg-background">
                                    {project.location?.lat != null && project.location?.lng != null ? (
                                        <>
                                            {mapLoadError && (
                                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-red-600 p-6 text-center">
                                                    <MapPin size={48} className="mb-4 opacity-50" />
                                                    <p className="text-sm font-medium">Map could not load. Check API key.</p>
                                                    <a
                                                        href={`https://www.google.com/maps?q=${project.location.lat},${project.location.lng}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="mt-2 text-secondary text-sm font-medium hover:underline"
                                                    >
                                                        Open in Google Maps
                                                    </a>
                                                </div>
                                            )}
                                            {!mapLoadError && !isMapLoaded && (
                                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-500 p-6">
                                                    <div className="w-10 h-10 border-2 border-secondary border-t-transparent rounded-full animate-spin mb-4" />
                                                    <p className="text-sm font-medium">Loading map...</p>
                                                </div>
                                            )}
                                            {!mapLoadError && isMapLoaded && (
                                                <>
                                                    <div className="absolute inset-0">
                                                        <GoogleMap
                                                            mapContainerStyle={MAP_CONTAINER_STYLE}
                                                            center={{ lat: project.location.lat, lng: project.location.lng }}
                                                            zoom={15}
                                                            options={{
                                                                zoomControl: true,
                                                                mapTypeControl: true,
                                                                mapTypeControlOptions: {
                                                                    position: window.google.maps.ControlPosition.INLINE_START_BLOCK_END,
                                                                    style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                                                                },
                                                                fullscreenControl: true,
                                                                streetViewControl: true,
                                                            }}
                                                        >
                                                            <Marker
                                                                position={{ lat: project.location.lat, lng: project.location.lng }}
                                                                animation={2}
                                                            />
                                                        </GoogleMap>
                                                    </div>
                                                    <div className="absolute top-4 left-4 z-10 w-[280px] min-h-[50px] bg-white rounded-sm shadow-lg border-l-4 border-secondary flex flex-col justify-center px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <MapPin size={20} className="text-secondary flex-shrink-0" />
                                                            <span className="font-serif font-semibold text-primary text-sm">{project.title}</span>
                                                        </div>
                                                    </div>
                                                    <a
                                                        href={`https://www.google.com/maps?q=${project.location.lat},${project.location.lng}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="absolute bottom-4 right-4 z-10 px-3 py-2 bg-primary text-white text-xs font-bold uppercase tracking-wider rounded opacity-90 hover:opacity-100 hover:bg-secondary transition-colors flex items-center gap-2"
                                                    >
                                                        <ExternalLink size={14} /> Open in Maps
                                                    </a>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-500 p-6">
                                            <MapPin size={48} className="text-gray-300 mb-4" />
                                            <p className="text-sm font-medium">Map will appear when location is set.</p>
                                            <p className="text-xs mt-1">Update project location in admin to see the map here.</p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </Container>
            </motion.section>
        </div>
    );
};

export default ProjectDetailsPage;
